// เนื้อเรื่องเฉพาะกลุ่มย่อยของสัตว์เลื้อยคลานและสัตว์สะเทินน้ำสะเทินบก

import type { GameEvent } from "../../engine/types";

const R = ["REPTILE"] as const;
const A = ["AMPHIBIAN"] as const;

export const REPTILE_AMPHIBIAN_SUBGROUP_EVENTS: readonly GameEvent[] = [
  // ---------- งู ----------
  {
    id: "snake_tongue",
    archetypes: R,
    stages: ["hatchling", "juvenile", "adult", "elder"],
    requires: { subgroups: ["reptile.snake"] },
    weight: 6,
    text: "คุณแลบลิ้นรับกลิ่นในอากาศ มีร่องรอยของเหยื่อผ่านมาเมื่อไม่นาน",
    fact: "งูใช้ลิ้นเก็บกลิ่นในอากาศแล้วส่งไปยังอวัยวะรับกลิ่นบนเพดานปาก ลิ้นสองแฉกช่วยบอกทิศทางของกลิ่นได้",
    choices: [
      {
        label: "ตามรอยกลิ่นไป",
        outcomes: [
          { chance: 0.7, text: "รอยกลิ่นพาคุณไปถึงรังหนู", effects: { fullness: 40 } },
          { chance: 0.3, text: "รอยกลิ่นจางหายไปกลางทาง", effects: { fullness: -5 } },
        ],
      },
      { label: "ซุ่มรอตรงเส้นทางกลิ่น", outcomes: [{ chance: 1, text: "เหยื่อเดินกลับมาทางเดิมจริงๆ", effects: { fullness: 30 } }] },
    ],
  },
  {
    id: "snake_big_meal",
    archetypes: R,
    stages: ["juvenile", "adult", "elder"],
    requires: { subgroups: ["reptile.snake"] },
    weight: 4,
    text: "เหยื่อตรงหน้าตัวใหญ่กว่าหัวคุณหลายเท่า",
    choices: [
      {
        label: "ค่อยๆ กลืนทั้งตัว",
        outcomes: [
          { chance: 0.8, text: "ขากรรไกรที่ยืดได้ทำให้คุณกลืนมันได้ อาหารมื้อนี้อยู่ได้หลายสัปดาห์ แต่ตัวหนักจนเคลื่อนไหวช้า", effects: { fullness: 60, safety: -15 } },
          { chance: 0.2, text: "มันใหญ่เกินไป คุณต้องคายทิ้ง", effects: { fullness: -10 } },
        ],
      },
      { label: "ปล่อยไป หาตัวเล็กกว่านี้", outcomes: [{ chance: 1, text: "คุณได้กบตัวเล็กๆ พอประทังชีวิต", effects: { fullness: 20 } }] },
    ],
  },
  {
    id: "snake_human",
    archetypes: R,
    stages: ["juvenile", "adult", "elder"],
    requires: { subgroups: ["reptile.snake"], habitat: ["forest", "grassland", "urban"] },
    weight: 2.5,
    text: "คุณเลื้อยเข้ามาใกล้บ้านคน มีคนกรีดร้องและวิ่งไปหยิบไม้",
    choices: [
      {
        label: "เลื้อยหนีเข้าพุ่มไม้",
        outcomes: [
          { chance: 0.8, text: "คุณหายเข้าไปในพุ่มไม้ทัน", effects: { safety: -10 } },
          { chance: 0.2, text: "ไม้ฟาดโดนหางคุณ", effects: { health: -25, cause: "human" } },
        ],
      },
      {
        label: "แผ่แม่เบี้ยขู่",
        outcomes: [
          { chance: 0.4, text: "เขาถอยไปเรียกเจ้าหน้าที่มาจับคุณไปปล่อยในป่าไกลๆ", effects: { safety: 10, fullness: -10 } },
          { chance: 0.5, text: "เขาตกใจแต่ไม่ยอมถอย คุณโดนตีจนบาดเจ็บ", effects: { health: -30, cause: "human" } },
          { chance: 0.1, text: "คนมาช่วยกันรุม", effects: { death: "human" } },
        ],
      },
    ],
  },
  {
    id: "snake_mongoose",
    archetypes: R,
    stages: ["hatchling", "juvenile", "adult"],
    requires: { subgroups: ["reptile.snake"] },
    weight: 3,
    text: "พังพอนตัวหนึ่งย่องเข้ามา ตาจ้องคุณไม่กะพริบ",
    choices: [
      {
        label: "ขู่และฉกใส่",
        outcomes: [
          { chance: 0.5, text: "พังพอนคิดว่าไม่คุ้ม และวิ่งจากไป", effects: { safety: 5 } },
          { chance: 0.4, text: "มันหลบการฉกได้หมด และกัดคุณที่ลำตัว", effects: { health: -30, cause: "predator" } },
          { chance: 0.1, text: "พังพอนว่องไวกว่าคุณ", effects: { death: "predator" } },
        ],
      },
      {
        label: "มุดลงรูที่ใกล้ที่สุด",
        outcomes: [
          { chance: 0.75, text: "คุณมุดหายลงไปทัน", effects: { safety: -5 } },
          { chance: 0.25, text: "มันคาบหางคุณไว้ คุณดิ้นจนหลุด", effects: { health: -20, cause: "predator" } },
        ],
      },
    ],
  },

  // ---------- กิ้งก่าและตุ๊กแก ----------
  {
    id: "lizard_tail_drop",
    archetypes: R,
    stages: ["hatchling", "juvenile", "adult"],
    requires: { subgroups: ["reptile.lizard"], notFlags: ["tail_lost"] },
    weight: 5,
    text: "นกตัวหนึ่งโฉบลงมาจิกหางคุณ",
    fact: "จิ้งจกและกิ้งก่าหลายชนิดสลัดหางทิ้งได้ หางที่หลุดยังดิ้นต่อเพื่อหลอกนักล่า แล้วหางใหม่จะค่อยๆ งอกขึ้นมา",
    choices: [
      { label: "สลัดหางทิ้ง!", outcomes: [{ chance: 1, text: "หางของคุณหลุดและดิ้นไปมา นกหันไปจิกหางแทน คุณหนีรอด", effects: { safety: 10, addFlags: ["tail_lost"] } }] },
      {
        label: "วิ่งหนีทั้งหาง",
        outcomes: [
          { chance: 0.5, text: "คุณมุดเข้าซอกหินทัน", effects: { safety: 5 } },
          { chance: 0.5, text: "นกจิกโดนหลัง", effects: { health: -25, cause: "predator" } },
        ],
      },
    ],
  },
  {
    id: "lizard_tail_regrow",
    archetypes: R,
    stageTags: ["young", "adult", "old"],
    requires: { subgroups: ["reptile.lizard"], flags: ["tail_lost"] },
    weight: 5,
    text: "หางใหม่ของคุณงอกออกมาแล้ว แม้จะสีไม่เหมือนเดิม",
    choices: [{ label: "กินให้มากเพื่อสร้างหางใหม่", outcomes: [{ chance: 1, text: "หางใหม่ยาวเต็มที่ พร้อมใช้หลอกนักล่าอีกครั้ง", effects: { fullness: -15, removeFlags: ["tail_lost"] } }] }],
  },
  {
    id: "lizard_lamp_hunt",
    archetypes: R,
    stages: ["juvenile", "adult", "elder"],
    requires: { subgroups: ["reptile.lizard"], habitat: ["urban"] },
    weight: 6,
    text: "หลอดไฟหน้าบ้านเปิดแล้ว แมลงบินมาตอมเต็มไปหมด",
    choices: [
      { label: "เกาะผนังรอใต้หลอดไฟ", outcomes: [{ chance: 1, text: "บุฟเฟ่ต์แมลงทั้งคืน", effects: { fullness: 40 } }] },
      {
        label: "ไล่จับบนเพดาน",
        outcomes: [
          { chance: 0.8, text: "เท้าที่เกาะผนังได้ทำให้คุณวิ่งบนเพดานได้สบาย", effects: { fullness: 30 } },
          { chance: 0.2, text: "คุณพลัดตกจากเพดาน", effects: { health: -15, cause: "accident" } },
        ],
      },
    ],
  },
  {
    id: "lizard_display",
    archetypes: R,
    stages: ["adult"],
    requires: { subgroups: ["reptile.lizard"], notFlags: ["has_mate"] },
    weight: 7,
    text: "ฤดูผสมพันธุ์มาถึง คุณเห็นตัวที่ถูกใจอยู่บนก้อนหินใกล้ๆ",
    choices: [
      {
        label: "ผงกหัวและกางเหนียงอวดสี",
        outcomes: [
          { chance: 0.65, text: "มันผงกหัวตอบ คุณได้คู่แล้ว", effects: { addFlags: ["has_mate"] } },
          { chance: 0.35, text: "ตัวผู้อีกตัววิ่งมาไล่คุณออกไป", effects: { safety: -10, fullness: -5 } },
        ],
      },
      {
        label: "ร้องเสียงดัง",
        outcomes: [{ chance: 1, text: "เสียงของคุณดังไปทั่ว มีตัวหนึ่งร้องตอบกลับมา", effects: { addFlags: ["has_mate"], safety: -5 } }],
      },
    ],
  },

  // ---------- เต่า ----------
  {
    id: "turtle_lost_years",
    archetypes: R,
    stages: ["juvenile"],
    requires: { subgroups: ["reptile.turtle"], habitat: ["sea"] },
    weight: 6,
    text: "คุณลอยไปกับกอสาหร่ายกลางมหาสมุทร ไม่เห็นฝั่งมาหลายเดือนแล้ว",
    fact: "ลูกเต่าทะเลใช้ชีวิตช่วงแรกล่องลอยกลางมหาสมุทรนานหลายปี นักวิทยาศาสตร์เรียกช่วงนี้ว่า \"ปีที่หายไป\" เพราะติดตามได้ยาก",
    choices: [
      { label: "ซ่อนตัวในกอสาหร่าย", outcomes: [{ chance: 1, text: "กอสาหร่ายมีทั้งอาหารและที่หลบภัย", effects: { fullness: 20, safety: 15 } }] },
      {
        label: "ว่ายออกไปหาอาหารที่อื่น",
        outcomes: [
          { chance: 0.6, text: "คุณเจอแมงกะพรุนลอยมาเป็นฝูง", effects: { fullness: 35 } },
          { chance: 0.4, text: "ปลาใหญ่ไล่ตามคุณ", effects: { safety: -20 } },
        ],
      },
    ],
  },
  {
    id: "turtle_jellyfish",
    archetypes: R,
    stages: ["juvenile", "adult", "elder"],
    requires: { subgroups: ["reptile.turtle"], habitat: ["sea"] },
    weight: 5,
    threatenedWeight: 1.5,
    text: "ข้างหน้ามีของใสๆ ลอยพลิ้วอยู่ในน้ำ ดูเหมือนแมงกะพรุน",
    fact: "ถุงพลาสติกในทะเลดูคล้ายแมงกะพรุนมาก เต่าทะเลจำนวนมากกินเข้าไปจนลำไส้อุดตัน",
    choices: [
      {
        label: "งับเลย",
        outcomes: [
          { chance: 0.5, text: "แมงกะพรุนจริงๆ อร่อย", effects: { fullness: 30 } },
          { chance: 0.4, text: "มันคือถุงพลาสติก ท้องของคุณปั่นป่วน", effects: { health: -30, cause: "human" } },
          { chance: 0.1, text: "พลาสติกอุดลำไส้จนกินอะไรไม่ได้อีก", effects: { death: "human" } },
        ],
      },
      { label: "ว่ายเข้าไปดูใกล้ๆ ก่อน", outcomes: [{ chance: 1, text: "คุณมองออกว่าเป็นถุงพลาสติก และปล่อยมันไป", effects: { fullness: -5 } }] },
    ],
  },
  {
    id: "turtle_natal_beach",
    archetypes: R,
    stages: ["adult"],
    // เต่าทะเลที่อพยพจริง (src/data/migrants.ts) ว่ายกลับหาดเกิดด้วย turtle_breeding_migration แทน
    requires: { subgroups: ["reptile.turtle"], habitat: ["sea"], notFlags: ["has_mate"], migration: "resident" },
    weight: 6,
    text: "สัญชาตญาณบางอย่างเรียกคุณให้ว่ายกลับไปยังชายหาดที่คุณเคยฟักออกมา",
    fact: "เต่าทะเลตัวเมียมักกลับมาวางไข่ที่ชายหาดเดียวกับที่ตัวเองฟักออกมา แม้จะผ่านไปหลายสิบปี",
    choices: [
      {
        label: "ว่ายกลับบ้านเกิด",
        outcomes: [
          { chance: 0.75, text: "หลังว่ายมาหลายพันกิโลเมตร คุณพบคู่นอกชายฝั่งบ้านเกิด", effects: { addFlags: ["has_mate"], fullness: -20 } },
          { chance: 0.25, text: "ชายหาดเปลี่ยนไปมาก เต็มไปด้วยโรงแรมและแสงไฟ", effects: { fullness: -20, safety: -10 } },
        ],
      },
      { label: "อยู่หากินต่อ", outcomes: [{ chance: 1, text: "คุณสะสมพลังงานไว้ก่อน", effects: { fullness: 20 } }] },
    ],
  },
  // ---------- เต่าทะเลอพยพ: ฟักบนหาด → ออกทะเล → กลับมาวางไข่ที่หาดเดิม (src/geo/migration.ts natalBeachGround) ----------
  {
    id: "turtle_swim_frenzy",
    archetypes: R,
    stages: ["hatchling", "juvenile"],
    requires: { subgroups: ["reptile.turtle"], migration: "due_b" },
    weight: 40,
    text: "พ้นคลื่นแนวแรกมาได้ ทะเลข้างหน้ากว้างไม่มีที่สิ้นสุด ร่างกายของคุณบอกให้ว่ายออกไปให้ไกลจากฝั่งที่สุด",
    fact: "ลูกเต่าทะเลว่ายไม่หยุดพักนานหนึ่งถึงสองวันหลังลงน้ำ เพื่อให้พ้นน้ำตื้นที่มีนักล่าชุกชุม แล้วปล่อยตัวไปกับกระแสน้ำสู่กลางมหาสมุทร",
    choices: [
      {
        label: "ว่ายต่อไม่หยุดทั้งวันทั้งคืน",
        outcomes: [
          { chance: 0.7, text: "คุณว่ายจนหมดแรง แล้วปล่อยตัวลอยไปกับกระแสน้ำจนถึง{destination}", effects: { fullness: -20, move: "migrate" } },
          { chance: 0.3, text: "ปลาตัวใหญ่ไล่งับคุณในน้ำตื้น คุณรอดมาได้ แต่ครีบขาดไปนิดหนึ่ง แล้วลอยตามกระแสน้ำไปถึง{destination}", effects: { fullness: -20, health: -30, move: "migrate", cause: "predator" } },
        ],
      },
      {
        label: "หลบอยู่ใต้กอสาหร่ายใกล้ฝั่ง",
        outcomes: [
          { chance: 0.6, text: "คุณพักได้สักพัก แต่ปลาที่หากินในน้ำตื้นว่ายวนเข้ามาใกล้เรื่อยๆ", effects: { fullness: 5, safety: -15 } },
          { chance: 0.4, text: "ปูตัวหนึ่งเดินมาเจอคุณใต้กอสาหร่าย", effects: { health: -35, cause: "predator" } },
        ],
      },
    ],
  },
  {
    id: "turtle_breeding_migration",
    archetypes: R,
    stages: ["adult"],
    requires: { subgroups: ["reptile.turtle"], migration: "due_a" },
    weight: 40,
    text: "หลายปีผ่านไปที่{destination}ไกลออกไป วันนี้สัญชาตญาณเรียกคุณให้กลับไปวางไข่ที่ชายหาดที่คุณฟักออกมา",
    fact: "เต่าทะเลจำ \"ลายเซ็น\" สนามแม่เหล็กโลกของชายหาดที่ตัวเองฟักไว้ตั้งแต่เป็นลูกเต่า แล้วใช้หาทางกลับมาวางไข่ที่เดิมได้แม้ผ่านไปหลายสิบปี",
    choices: [
      {
        label: "ว่ายกลับบ้านเกิด",
        outcomes: [
          { chance: 0.7, text: "คุณว่ายข้ามทะเลไปหลายสัปดาห์ ระหว่างทางเจอคู่ และไปถึงทะเลหน้าหาดบ้านเกิด", effects: { fullness: -30, move: "migrate", addFlags: ["has_mate"] } },
          { chance: 0.2, text: "คุณติดอวนลอยของเรือประมง กว่าจะดิ้นหลุดได้ก็เกือบหมดลม แล้วว่ายต่อจนถึงหาดบ้านเกิด", effects: { fullness: -30, health: -35, move: "migrate", cause: "human" } },
          { chance: 0.1, text: "ใบพัดเรือเฉี่ยวกระดองของคุณเป็นแผลยาว คุณต้องกลับไปพักฟื้นที่แหล่งหากินก่อน", effects: { fullness: -15, health: -40, cause: "human" } },
        ],
      },
      { label: "หากินต่ออีกปี", outcomes: [{ chance: 1, text: "คุณกินหญ้าทะเลและแมงกะพรุนสะสมพลังงานไว้ วางไข่ปีหน้าก็ยังทัน", effects: { fullness: 25 } }] },
    ],
  },
  {
    id: "turtle_nest",
    archetypes: R,
    stages: ["adult"],
    requires: { subgroups: ["reptile.turtle"], migration: "due_b" },
    weight: 40,
    text: "คืนนี้เป็นคืนเดือนมืด คลื่นซัดเบาๆ บนชายหาดที่คุณฟักออกมาเมื่อหลายสิบปีก่อน",
    fact: "เต่าทะเลตัวเมียขึ้นวางไข่หลายรังในฤดูเดียว แต่ละรังห่างกันราวสองสัปดาห์ ลูกเต่าทะเลราว 1 ใน 1,000 ตัวเท่านั้นที่รอดจนโตเต็มวัย",
    choices: [
      {
        label: "คลานขึ้นหาดไปวางไข่เหนือแนวน้ำขึ้น",
        outcomes: [
          { chance: 0.75, text: "คุณใช้ครีบหลังขุดหลุมลึกเท่าแขน วางไข่ {n} ฟอง กลบทรายให้เรียบ แล้วคลานกลับลงทะเลไปยัง{destination}", effects: { offspring: "clutch", addFlags: ["parent"], removeFlags: ["has_mate"], fullness: -20, move: "migrate" } },
          { chance: 0.25, text: "ไฟฉายของคนลักลอบเก็บไข่ส่องมาตอนคุณวางไข่เสร็จ คุณรีบกลบหลุมที่มีไข่ {n} ฟองแล้วคลานกลับลงทะเลไปยัง{destination}", effects: { offspring: "clutch", addFlags: ["parent"], removeFlags: ["has_mate"], fullness: -20, safety: -15, move: "migrate" } },
        ],
      },
      { label: "ชายหาดสว่างและมีคนเยอะ ว่ายกลับไปก่อน", outcomes: [{ chance: 1, text: "คุณว่ายวนอยู่หน้าหาด แล้วกลับไปยัง{destination}โดยไม่ได้วางไข่", effects: { fullness: -15, move: "migrate" } }] },
    ],
  },
  {
    id: "turtle_shell",
    archetypes: R,
    stages: ["hatchling", "juvenile", "adult", "elder"],
    requires: { subgroups: ["reptile.turtle"], habitat: ["freshwater"] },
    weight: 4,
    text: "นากตัวหนึ่งว่ายตรงเข้ามาหาคุณ",
    fact: "เต่าน้ำจืดหลายชนิดหดหัวและขาเข้ากระดองได้ แต่เต่าทะเลทำแบบนั้นไม่ได้",
    choices: [
      {
        label: "หดหัวเข้ากระดอง",
        outcomes: [
          { chance: 0.8, text: "นากพลิกคุณไปมา แล้วก็ยอมแพ้", effects: { safety: 5 } },
          { chance: 0.2, text: "กระดองของคุณยังไม่แข็งพอ มันกัดเป็นรอย", effects: { health: -25, cause: "predator" } },
        ],
      },
      { label: "ดำลงซุกโคลน", outcomes: [{ chance: 1, text: "โคลนพรางตัวคุณจนนากหาไม่เจอ", effects: { safety: 10, fullness: -5 } }] },
    ],
  },

  // ---------- จระเข้ ----------
  {
    id: "croc_mother_carry",
    opening: true,
    archetypes: R,
    stages: ["hatchling"],
    requires: { subgroups: ["reptile.crocodile"] },
    once: true,
    weight: 30,
    text: "คุณร้องจากในไข่ แม่ได้ยินและขุดรังออกมา",
    fact: "แม่จระเข้เฝ้ารังและคาบลูกที่เพิ่งฟักไว้ในปากอย่างเบามือ เพื่อพาลงน้ำ",
    choices: [
      { label: "ปีนเข้าไปในปากแม่", outcomes: [{ chance: 1, text: "แม่คาบคุณไว้อย่างเบามือ แล้วพาลงน้ำอย่างปลอดภัย", effects: { safety: 15 } }] },
      {
        label: "คลานลงน้ำเอง",
        outcomes: [
          { chance: 0.6, text: "คุณถึงน้ำ แม่ว่ายตามมาเฝ้า" },
          { chance: 0.4, text: "นกกระยางตัวใหญ่ดักรออยู่ริมน้ำ แม่ไล่มันไปทัน", effects: { safety: -15 } },
        ],
      },
    ],
  },
  {
    id: "croc_creche",
    archetypes: R,
    stages: ["hatchling", "juvenile"],
    requires: { subgroups: ["reptile.crocodile"] },
    weight: 6,
    text: "คุณกับพี่น้องหลายสิบตัวลอยอยู่ด้วยกันในน้ำตื้นใกล้ๆ แม่",
    choices: [
      { label: "อยู่ในกลุ่มใกล้แม่", outcomes: [{ chance: 1, text: "ถ้ามีภัย แค่ร้องเรียก แม่จะพุ่งเข้ามาทันที", effects: { safety: 15 } }] },
      {
        label: "ออกไปล่าแมลงเอง",
        outcomes: [
          { chance: 0.7, text: "คุณจับแมลงปอที่บินต่ำได้หลายตัว", effects: { fullness: 25 } },
          { chance: 0.3, text: "ปลาช่อนตัวใหญ่ไล่งับ", effects: { health: -20, cause: "predator" } },
        ],
      },
    ],
  },
  {
    id: "croc_ambush",
    archetypes: R,
    stages: ["adult", "elder"],
    requires: { subgroups: ["reptile.crocodile"] },
    weight: 7,
    text: "คุณลอยนิ่งอยู่ใต้ผิวน้ำ โผล่แค่ตากับรูจมูก สัตว์ตัวหนึ่งกำลังเดินลงมากินน้ำ",
    choices: [
      {
        label: "รอจนมันก้มลงกินน้ำ",
        outcomes: [
          { chance: 0.6, text: "จู่โจมเดียว คุณได้อาหารที่อยู่ได้หลายสัปดาห์", effects: { fullness: 60 } },
          { chance: 0.4, text: "มันรู้ตัวก่อนและกระโดดหนีไป", effects: { fullness: -5 } },
        ],
      },
      { label: "ว่ายไปจับปลาแทน", outcomes: [{ chance: 1, text: "ปลาเยอะในฤดูนี้", effects: { fullness: 25 } }] },
    ],
  },
  {
    id: "croc_gape",
    archetypes: R,
    stages: ["juvenile", "adult", "elder"],
    requires: { subgroups: ["reptile.crocodile"] },
    weight: 4,
    text: "แดดเที่ยงร้อนจัด คุณนอนอยู่บนตลิ่ง",
    fact: "จระเข้มักนอนอ้าปากบนตลิ่ง เชื่อว่าเป็นวิธีช่วยระบายความร้อนออกจากร่างกาย",
    choices: [
      { label: "นอนอ้าปากระบายความร้อน", outcomes: [{ chance: 1, text: "ร่างกายเย็นลงพอดี", effects: { health: 10 } }] },
      { label: "ลงไปแช่น้ำ", outcomes: [{ chance: 1, text: "น้ำเย็นสบาย", effects: { health: 5, safety: 5 } }] },
    ],
  },

  // ---------- กบและคางคก ----------
  {
    id: "frog_snake",
    archetypes: A,
    stages: ["adult", "elder"],
    requires: { subgroups: ["amphibian.frog"] },
    weight: 5,
    text: "งูเขียวตัวหนึ่งเลื้อยมาตามกอหญ้า ห่างคุณไม่กี่ช่วงตัว",
    choices: [
      {
        label: "กระโดดลงน้ำ",
        outcomes: [
          { chance: 0.8, text: "ขาหลังที่แข็งแรงพาคุณลงน้ำในกระโดดเดียว", effects: { safety: 5 } },
          { chance: 0.2, text: "งูฉกโดนขา คุณดิ้นหลุดมาได้", effects: { health: -25, cause: "predator" } },
        ],
      },
      {
        label: "อยู่นิ่งๆ อาศัยสีพรางตัว",
        outcomes: [
          { chance: 0.6, text: "งูเลื้อยผ่านไปโดยไม่เห็นคุณ", effects: { safety: 10 } },
          { chance: 0.3, text: "งูหันมาเห็นพอดี คุณกระโดดหนีทันอย่างหวุดหวิด", effects: { safety: -15 } },
          { chance: 0.1, text: "งูเห็นคุณก่อน", effects: { death: "predator" } },
        ],
      },
    ],
  },
  {
    id: "frog_dry_season",
    archetypes: A,
    stages: ["adult", "elder"],
    requires: { subgroups: ["amphibian.frog"] },
    weight: 4,
    text: "ฤดูแล้งมาถึง ทุ่งนาแห้งแตกระแหง",
    fact: "กบหลายชนิดขุดรูลงไปในดินโคลนแล้วจำศีลตลอดฤดูแล้ง ก่อนจะออกมาเมื่อฝนแรกตก",
    choices: [
      { label: "ขุดรูลงไปจำศีลในโคลน", outcomes: [{ chance: 1, text: "คุณหลับยาวใต้ดินชื้นๆ จนฝนแรกปลุกคุณขึ้นมา", effects: { fullness: -10, safety: 15 } }] },
      {
        label: "ออกหาแหล่งน้ำใหม่",
        outcomes: [
          { chance: 0.5, text: "คุณเจอบ่อน้ำที่ยังไม่แห้ง", effects: { fullness: 15 } },
          { chance: 0.5, text: "แดดเผาจนผิวแห้ง", effects: { health: -25, cause: "disaster" } },
        ],
      },
    ],
  },
  {
    id: "frog_pesticide",
    archetypes: A,
    stages: ["tadpole", "adult"],
    requires: { subgroups: ["amphibian.frog"] },
    weight: 2,
    threatenedWeight: 2,
    text: "มีกลิ่นฉุนลอยมาจากแปลงนาข้างๆ น้ำเริ่มมีฟองแปลกๆ",
    choices: [
      { label: "ย้ายไปอยู่ไกลจากแปลงนา", outcomes: [{ chance: 1, text: "คุณย้ายไปอยู่คลองที่น้ำใสกว่า", effects: { fullness: -10 } }] },
      {
        label: "อยู่ที่เดิม อาหารเยอะ",
        outcomes: [
          { chance: 0.4, text: "ไม่เป็นอะไร", effects: { fullness: 15 } },
          { chance: 0.5, text: "สารเคมีซึมผ่านผิว คุณป่วยหนัก", effects: { health: -30, cause: "human" } },
          { chance: 0.1, text: "สารเคมีเข้มข้นเกินไป", effects: { death: "human" } },
        ],
      },
    ],
  },
  {
    id: "frog_chytrid",
    archetypes: A,
    stages: ["adult", "elder"],
    requires: { subgroups: ["amphibian.frog", "amphibian.salamander"] },
    weight: 1,
    threatenedWeight: 3,
    text: "ผิวของคุณเริ่มลอกเป็นแผ่นและหนาขึ้นผิดปกติ",
    fact: "เชื้อราไคทริดทำให้ผิวของสัตว์สะเทินน้ำสะเทินบกหนาขึ้นจนหายใจทางผิวหนังไม่ได้ และทำให้กบหลายชนิดทั่วโลกสูญพันธุ์",
    choices: [
      {
        label: "แช่น้ำอุ่นในที่มีแดด",
        outcomes: [
          { chance: 0.5, text: "ความอุ่นช่วยชะลอเชื้อรา อาการดีขึ้น", effects: { health: -5 } },
          { chance: 0.5, text: "เชื้อราลุกลามต่อ", effects: { health: -35, cause: "disease" } },
        ],
      },
      { label: "ทำตัวตามปกติ", outcomes: [{ chance: 1, text: "ผิวของคุณแย่ลงเรื่อยๆ", effects: { health: -40, cause: "disease" } }] },
    ],
  },

  // ---------- ซาลาแมนเดอร์และกระท่าง ----------
  {
    id: "salamander_regrow",
    archetypes: A,
    stages: ["tadpole", "adult", "elder"],
    requires: { subgroups: ["amphibian.salamander"], flags: ["injured"] },
    weight: 8,
    text: "ขาที่บาดเจ็บของคุณเริ่มมีเนื้อเยื่อใหม่งอกออกมา",
    fact: "ซาลาแมนเดอร์หลายชนิดงอกขา หาง และแม้แต่บางส่วนของหัวใจขึ้นมาใหม่ได้",
    choices: [{ label: "พักให้ขางอกใหม่", outcomes: [{ chance: 1, text: "ขาใหม่งอกออกมาสมบูรณ์เหมือนเดิม", effects: { health: 30, fullness: -10, removeFlags: ["injured"] } }] }],
  },
  {
    id: "salamander_stream",
    archetypes: A,
    stages: ["adult", "elder"],
    requires: { subgroups: ["amphibian.salamander"] },
    weight: 6,
    text: "ลำธารบนภูเขาเย็นเฉียบ มีตัวอ่อนแมลงซ่อนอยู่ใต้ก้อนหิน",
    fact: "กระท่างน้ำในไทยอาศัยอยู่ตามแหล่งน้ำบนภูเขาสูงที่อากาศเย็น เช่น ดอยอินทนนท์ และเป็นสัตว์คุ้มครอง",
    choices: [
      { label: "พลิกหินหาอาหาร", outcomes: [{ chance: 1, text: "ใต้หินเต็มไปด้วยอาหาร", effects: { fullness: 35 } }] },
      { label: "ซ่อนตัวใต้ใบไม้เปียก", outcomes: [{ chance: 1, text: "ผิวของคุณชุ่มชื้นและปลอดภัย", effects: { fullness: 10, health: 5, safety: 10 } }] },
    ],
  },
  {
    id: "salamander_poison",
    archetypes: A,
    stages: ["adult", "elder"],
    requires: { subgroups: ["amphibian.salamander"] },
    weight: 4,
    text: "นกตัวหนึ่งคาบคุณขึ้นมาจากพื้น",
    fact: "ซาลาแมนเดอร์และกระท่างหลายชนิดมีพิษที่ผิวหนัง และมักมีสีสดเพื่อเตือนนักล่า",
    choices: [
      {
        label: "ปล่อยพิษจากผิวหนัง",
        outcomes: [
          { chance: 0.8, text: "นกคายคุณทิ้งทันที รสชาติของคุณแย่เกินกว่าจะกิน", effects: { health: -10, cause: "predator" } },
          { chance: 0.2, text: "นกตัวนี้ทนพิษได้", effects: { death: "predator" } },
        ],
      },
    ],
  },
  {
    id: "salamander_rain",
    archetypes: A,
    stages: ["adult"],
    requires: { subgroups: ["amphibian.salamander", "amphibian.caecilian"], notFlags: ["has_mate"] },
    weight: 6,
    text: "ฝนแรกของฤดูตกลงมา ถึงเวลาเดินทางกลับไปยังแอ่งน้ำที่คุณเกิด",
    choices: [
      {
        label: "เดินทางกลับแอ่งน้ำบ้านเกิด",
        outcomes: [
          { chance: 0.7, text: "คุณไปถึงและพบคู่ที่นั่น", effects: { addFlags: ["has_mate"], fullness: -10 } },
          { chance: 0.3, text: "ถนนตัดผ่านเส้นทางเดิม คุณเกือบโดนรถทับ", effects: { health: -20, cause: "human" } },
        ],
      },
      { label: "รอฝนหนักกว่านี้", outcomes: [{ chance: 1, text: "ฝนหยุดก่อนที่คุณจะออกเดินทาง" }] },
    ],
  },

  // ---------- เขียดงู ----------
  {
    id: "caecilian_skin_feed",
    opening: true,
    archetypes: A,
    stages: ["tadpole"],
    requires: { subgroups: ["amphibian.caecilian"] },
    once: true,
    weight: 15,
    text: "แม่ขดตัวอยู่รอบตัวคุณกับพี่น้องในรังใต้ดิน",
    fact: "ลูกเขียดงูบางชนิดกินผิวหนังชั้นนอกของแม่เป็นอาหาร โดยแม่จะสร้างผิวชั้นนี้ขึ้นมาใหม่เรื่อยๆ",
    choices: [{ label: "กินอาหารที่แม่เตรียมไว้", outcomes: [{ chance: 1, text: "คุณกินผิวชั้นนอกที่อุดมไปด้วยไขมันของแม่ และโตขึ้นอย่างรวดเร็ว", effects: { fullness: 40, safety: 10 } }] }],
  },
  {
    id: "caecilian_burrow",
    archetypes: A,
    stages: ["adult", "elder"],
    requires: { subgroups: ["amphibian.caecilian"] },
    weight: 8,
    text: "คุณใช้หัวที่แข็งแรงดันดินชื้นไปข้างหน้า มีไส้เดือนอยู่ใกล้ๆ",
    fact: "เขียดงูเป็นสัตว์สะเทินน้ำสะเทินบกที่ไม่มีขา ใช้ชีวิตขุดดิน และมักถูกเข้าใจผิดว่าเป็นงูหรือไส้เดือน",
    choices: [
      { label: "ไล่ตามไส้เดือน", outcomes: [{ chance: 1, text: "คุณงับไส้เดือนได้ทั้งตัว", effects: { fullness: 35 } }] },
      {
        label: "ขุดขึ้นไปใกล้ผิวดิน",
        outcomes: [
          { chance: 0.6, text: "ใกล้ผิวดินมีแมลงเยอะกว่า", effects: { fullness: 25 } },
          { chance: 0.4, text: "จอบของชาวสวนขุดลงมาใกล้ตัวคุณ", effects: { health: -20, cause: "human" } },
        ],
      },
    ],
  },
];
